import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchActiveConversationIds } from '@api/activeConversations';

const POLL_MS = 5_000;

/** Stable empty result, so the memo below keeps its reference between polls. */
const NO_IDS: readonly string[] = [];

/**
 * The conversations whose AI turn is still running, for the task list's
 * progress indicator. Polled because the work outlives the client: a turn
 * carries on in the background once the user navigates away, and a turn whose
 * orchestrator died stops being reported without anything telling us so.
 * Pauses in hidden tabs (React Query's default) and catches up on focus.
 */
export function useActiveConversations(): ReadonlySet<string> {
  const { data, isError } = useQuery({
    queryKey: ['conversations', 'active'],
    queryFn: fetchActiveConversationIds,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: POLL_MS,
    retry: false,
  });

  // A failed poll reports nothing rather than freezing the last answer, which
  // would spin forever if the endpoint stayed down.
  const ids = isError ? NO_IDS : (data ?? NO_IDS);

  return useMemo(() => new Set(ids), [ids]);
}
